import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setOnline } from 'Store/auth/networkSlice'

const { REACT_APP_URL } = process.env

/**
 * Header banner that appears when the network is offline or unstable.
 *
 * - Renders nothing on a healthy connection.
 * - Listens to the browser's `online` / `offline` events for instant
 *   feedback on connectivity loss.
 * - Reads `isUnreliable` from Redux, which the axios interceptor in
 *   BaseService sets after two consecutive failed API calls and clears on
 *   any successful response.
 * - Provides a Refresh button so the user can recover after a long outage.
 */
export default function NetworkBanner() {
    const dispatch = useDispatch()
    const { isOnline, isUnreliable } = useSelector(state => state.network)

    useEffect(() => {
        const goOnline = () => dispatch(setOnline(true))
        const goOffline = () => dispatch(setOnline(false))
        window.addEventListener('online', goOnline)
        window.addEventListener('offline', goOffline)
        return () => {
            window.removeEventListener('online', goOnline)
            window.removeEventListener('offline', goOffline)
        }
    }, [dispatch])

    // Continuous background connectivity probe.
    // Runs independently of user activity — no need to wait for the user to
    // trigger an API call before the banner reflects reality. Uses a plain
    // fetch (bypasses axios/interceptors) with `cache: 'no-store'` so we
    // actually hit the network, plus a 5s AbortController timeout so a slow
    // server also counts as "unreachable".
    //
    // Poll cadence:
    //   online  → every 60 s (light background heartbeat)
    //   offline → every  10 s (rapid recovery detection)
    useEffect(() => {
        let cancelled = false

        const probe = async () => {
            const controller = new AbortController()
            const timeout = setTimeout(() => controller.abort(), 5000)
            try {
                // Any HTTP response — including 401/404/405 — proves we can
                // reach the server. Only a network-layer failure counts as offline.
                await fetch(`${REACT_APP_URL}/api/list/app`, {
                    method: 'GET',
                    cache: 'no-store',
                    signal: controller.signal,
                })
                clearTimeout(timeout)
                if (cancelled) return
                if (!isOnline) dispatch(setOnline(true))
            } catch {
                clearTimeout(timeout)
                if (cancelled) return
                if (isOnline) dispatch(setOnline(false))
            }
        }

        // Fire immediately, then on an interval keyed to current state.
        probe()
        const id = setInterval(probe, isOnline ? 60000 : 10000)
        return () => {
            cancelled = true
            clearInterval(id)
        }
    }, [isOnline, dispatch])

    if (isOnline && !isUnreliable) return null

    const offline = !isOnline
    const tone = offline ? 'bg-danger text-white' : 'bg-warning text-black'
    const text = offline
        ? 'You are offline. The app will resume once your connection returns.'
        : 'Your network is slow or unstable. Some actions may fail.'

    return (
        <div
            className={`fixed top-0 inset-x-0 z-50 px-4 py-2 text-sm font-medium shadow ${tone}`}
            role="alert"
            aria-live="polite"
        >
            <div className="flex items-center justify-between gap-3 mx-auto max-w-screen-2xl">
                <span>{text}</span>
                {/* <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="px-3 py-1 text-xs font-semibold text-white bg-black rounded bg-opacity-20 hover:bg-opacity-30 whitespace-nowrap"
                >
                    Refresh page
                </button> */}
            </div>
        </div>
    )
}
